import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import { CouponDialog } from "@/components/coupon-dialog";
import { Pagination } from "@/components/pagination";

export const Route = createFileRoute("/_authenticated/coupons")({
  component: CouponsPage,
});

function formatCouponValue(type: string, value: number) {
  if (type === "percentage") return `${value}%`;
  return `${(value / 100).toFixed(2)} zł`;
}

type Coupon = {
  id: string;
  code: string;
  description: string | null;
  type: "percentage" | "fixed_amount";
  value: number;
  minPurchaseAmount: number | null;
  maxUses: number | null;
  usageCount: number;
  usesPerUser: number;
  treatmentIds: string[];
  tagIds: string[];
  appliesTo: string | null;
  scoped: boolean;
  audience: "code" | "all" | "selected";
  assignedCount: number;
  validFrom: Date | string | null;
  validUntil: Date | string | null;
  isActive: boolean;
};

function formatDay(value: Date | string) {
  return new Date(value).toLocaleDateString("pl-PL");
}

function audienceLabel(c: Pick<Coupon, "audience" | "assignedCount">) {
  if (c.audience === "all") return "Wszyscy klienci";
  if (c.audience === "selected") return `Wybrani · ${c.assignedCount}`;
  return `Na kod · ${c.assignedCount}`;
}

/** Where the coupon is in its validity window, if that is worth a badge. */
function validityState(c: Pick<Coupon, "validFrom" | "validUntil">) {
  const now = Date.now();
  if (c.validUntil && new Date(c.validUntil).getTime() < now) return "Wygasł";
  if (c.validFrom && new Date(c.validFrom).getTime() > now) return "Zaplanowany";
  return null;
}

function CouponsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = trpc.coupon.list.useQuery({ page, limit: 20 });
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);

  const toggleActive = trpc.coupon.update.useMutation({
    onSuccess: () => utils.coupon.list.invalidate(),
  });

  const deleteCoupon = trpc.coupon.delete.useMutation({
    onSuccess: () => {
      utils.coupon.list.invalidate();
      toast.success("Kupon usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kupony</h1>
          <p className="text-sm text-muted-foreground">{data?.total ?? 0} kuponów</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="mr-1 size-4" />
          Dodaj kupon
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Kod</th>
                <th className="px-4 py-3 font-medium">Rabat</th>
                <th className="px-4 py-3 font-medium">Odbiorcy</th>
                <th className="px-4 py-3 font-medium">Wykorzystane / pula</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Ważność</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((c) => {
                const validity = validityState(c);
                return (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <span className="font-mono font-medium">{c.code}</span>
                    {c.description && (
                      <p className="max-w-56 truncate text-xs text-muted-foreground">{c.description}</p>
                    )}
                    {c.scoped && !c.appliesTo && (
                      <p className="max-w-56 text-xs text-destructive">Zabiegi z zakresu kuponu zostały usunięte — kupon nie działa</p>
                    )}
                    {c.appliesTo && (
                      <p className="max-w-56 truncate text-xs text-muted-foreground">Tylko: {c.appliesTo}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatCouponValue(c.type, c.value)}
                    {c.minPurchaseAmount ? (
                      <p className="text-xs text-muted-foreground">od {formatCouponValue("fixed_amount", c.minPurchaseAmount)}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{audienceLabel(c)}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {c.usageCount}{c.maxUses ? ` / ${c.maxUses}` : ""}
                    {c.usesPerUser > 1 && (
                      <p className="text-xs text-muted-foreground">{c.usesPerUser}× na osobę</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={c.isActive ? "default" : "secondary"}
                      className="cursor-pointer"
                      onClick={() => toggleActive.mutate({ id: c.id, isActive: !c.isActive })}
                    >
                      {c.isActive ? "Aktywny" : "Nieaktywny"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.validFrom && <p>od {formatDay(c.validFrom)}</p>}
                    <p>{c.validUntil ? `do ${formatDay(c.validUntil)}` : "Bezterminowo"}</p>
                    {validity && (
                      <Badge variant="secondary" className="mt-1 text-xs">{validity}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setDialogOpen(true); }}>
                        Edytuj
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm("Usunąć kupon?")) {
                            deleteCoupon.mutate({ id: c.id });
                          }
                        }}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Brak kuponów</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} totalPages={data?.totalPages ?? 1} onPageChange={setPage} />
        </div>
      </Card>

      <CouponDialog open={dialogOpen} onOpenChange={setDialogOpen} coupon={editing} />
    </div>
  );
}
